/**
 * Panneau de feedback - Formulaire de saisie
 *
 * Glissement depuis la droite, non bloquant
 * Fermeture: croix, clic extérieur, Escape, après envoi réussi
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useFeedback } from './useFeedback';
import { useSpeechRecognition } from './useSpeechRecognition';

const MAX_CHARS = 2000;
const AUTO_CLOSE_DELAY = 2000; // 2 secondes après succès

export interface FeedbackPanelProps {
  /** Panneau ouvert ou fermé */
  isOpen: boolean;
  /** Callback de fermeture */
  onClose: () => void;
  /** Callback après envoi réussi */
  onFeedbackSent?: () => void;
}

export function FeedbackPanel({
  isOpen,
  onClose,
  onFeedbackSent,
}: FeedbackPanelProps) {
  const [content, setContent] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    status,
    isLoading,
    isSuccess,
    isError,
    errorMessage,
    retryAfter,
    submit,
    reset: resetFeedback,
  } = useFeedback();

  // Callback pour ajouter le texte dicté
  const handleTranscript = useCallback((text: string) => {
    setContent((prev) => {
      const newContent = prev + (prev && !prev.endsWith(' ') ? ' ' : '') + text;
      return newContent.slice(0, MAX_CHARS);
    });
  }, []);

  const {
    isSupported: isSpeechSupported,
    isListening,
    interimTranscript,
    error: speechError,
    startListening,
    stopListening,
    reset: resetSpeech,
  } = useSpeechRecognition(handleTranscript);

  // Focus sur le textarea à l'ouverture
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Fermeture avec Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Clic extérieur pour fermer
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Délai pour éviter la fermeture immédiate au clic d'ouverture
    const timeout = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Auto-fermeture après succès
  useEffect(() => {
    if (isSuccess) {
      onFeedbackSent?.();
      const timeout = setTimeout(() => {
        onClose();
        // Reset après fermeture
        setTimeout(() => {
          setContent('');
          resetFeedback();
          resetSpeech();
        }, 300);
      }, AUTO_CLOSE_DELAY);

      return () => clearTimeout(timeout);
    }
  }, [isSuccess, onClose, onFeedbackSent, resetFeedback, resetSpeech]);

  // Reset à la fermeture (si pas succès)
  useEffect(() => {
    if (!isOpen && status !== 'success') {
      // Conserver le contenu en cas de fermeture accidentelle
      resetFeedback();
    }
  }, [isOpen, status, resetFeedback]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isListening) {
      stopListening();
    }
    await submit(content);
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= MAX_CHARS) {
      setContent(newValue);
    }
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const currentRoute = typeof window !== 'undefined' ? window.location.pathname : '';
  const charCount = content.length;
  const charRemaining = MAX_CHARS - charCount;

  // Affichage du texte en cours de dictée
  const displayContent = content + (interimTranscript ? ` ${interimTranscript}` : '');

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay semi-transparent (ne bloque pas les clics, juste visuel) */}
      <div
        className="fixed inset-0 bg-black/20 z-40 transition-opacity duration-300"
        aria-hidden="true"
      />

      {/* Panneau */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        className={`
          fixed right-0 top-0 bottom-0 z-50
          w-full max-w-md
          bg-white shadow-2xl
          transform transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          flex flex-col
        `}
        style={{ maxHeight: '100vh' }}
      >
        {/* En-tête */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 id="feedback-title" className="text-lg font-semibold text-gray-900">
            Vos réactions
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer le panneau"
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Corps */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col p-4 overflow-y-auto">
          {/* Message de succès */}
          {isSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm font-medium">
                Merci pour votre retour !
              </p>
            </div>
          )}

          {/* Message d'erreur */}
          {isError && errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{errorMessage}</p>
              {retryAfter && (
                <p className="text-red-600 text-xs mt-1">
                  Réessayez dans {Math.ceil(retryAfter / 60)} minute(s)
                </p>
              )}
            </div>
          )}

          {/* Erreur dictée vocale */}
          {speechError && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">{speechError}</p>
            </div>
          )}

          {/* Textarea */}
          <div className="flex-1 flex flex-col">
            <textarea
              ref={textareaRef}
              value={interimTranscript ? displayContent : content}
              onChange={handleContentChange}
              placeholder="Décrivez votre ressenti, une difficulté, une suggestion..."
              disabled={isLoading || isSuccess}
              className={`
                flex-1 min-h-[120px] p-3
                border border-gray-300 rounded-lg
                resize-y
                text-gray-900 placeholder-gray-400
                focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent
                disabled:bg-gray-50 disabled:text-gray-500
                ${isListening ? 'border-red-400 ring-2 ring-red-200' : ''}
              `}
            />

            {/* Compteur de caractères */}
            <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
              <span>
                {charCount} / {MAX_CHARS} caractères
                {charRemaining <= 100 && charRemaining > 0 && (
                  <span className="text-amber-600 ml-1">
                    ({charRemaining} restants)
                  </span>
                )}
                {charRemaining === 0 && (
                  <span className="text-red-600 ml-1">(limite atteinte)</span>
                )}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
            {/* Bouton micro (si supporté) */}
            <div>
              {isSpeechSupported && (
                <button
                  type="button"
                  onClick={toggleListening}
                  disabled={isLoading || isSuccess}
                  aria-label={isListening ? 'Arrêter la dictée' : 'Démarrer la dictée vocale'}
                  className={`
                    p-3 rounded-full transition-all
                    ${isListening
                      ? 'bg-red-500 text-white animate-pulse'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Bouton envoyer */}
            <button
              type="submit"
              disabled={isLoading || isSuccess || content.trim().length === 0}
              className={`
                px-6 py-2.5
                bg-violet-600 hover:bg-violet-700
                text-white font-medium text-sm
                rounded-lg
                transition-all
                focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed
                ${isLoading ? 'cursor-wait' : ''}
              `}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Envoi...
                </span>
              ) : isSuccess ? (
                'Envoyé !'
              ) : (
                'Envoyer'
              )}
            </button>
          </div>

          {/* Info contextuelle */}
          <div className="mt-4 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              Page : {currentRoute}
            </p>
          </div>
        </form>
      </div>
    </>
  );
}

export default FeedbackPanel;
